import type { PhotoCoordinates } from './photoLocation';

type ExpoLocationModule = typeof import('expo-location');

// `expo-location` yeni bir native binary gerektirir. Dinamik import sayesinde
// modülü taşımayan eski 1.0.5 binary, kamera seçeneğini kullanmaya devam eder;
// yalnız çekim-anı konumu sessizce atlanır.
function loadLocationModule(): ExpoLocationModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-location') as ExpoLocationModule;
  } catch {
    return null;
  }
}

export async function requestForegroundLocationPermission(): Promise<boolean> {
  const Location = loadLocationModule();
  if (!Location) return false;

  try {
    const permission = await Location.requestForegroundPermissionsAsync();
    return permission.granted;
  } catch {
    return false;
  }
}

export async function coordinatesAtCapture(): Promise<PhotoCoordinates | null> {
  const Location = loadLocationModule();
  if (!Location) return null;

  try {
    if (!await requestForegroundLocationPermission()) return null;

    const position = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
    return {
      latitude: Number(latitude.toFixed(6)),
      longitude: Number(longitude.toFixed(6)),
    };
  } catch {
    // Konum, fotoğraf göndermenin zorunlu ön koşulu değildir. İzin reddi,
    // cihaz hizmetinin kapalı olması veya geçici ölçüm hatası fotoğrafı engellemez.
    return null;
  }
}

/**
 * Canlı paylaşım için ölçüm (9.3.3.3 · D-580).
 *
 * `coordinatesAtCapture`'dan ayrı duruyor çünkü burada hata payı ve hız da
 * gerekiyor: ikisi de ölçümün GÖNDERİLİP gönderilmeyeceğini belirliyor.
 * Fotoğrafta bu sorular yok, orada tek bir nokta yeter.
 */
export type LivePoint = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  speed: number | null;
};

export async function readLivePoint(): Promise<LivePoint | null> {
  const Location = loadLocationModule();
  if (!Location) return null;
  try {
    const position = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });
    const { latitude, longitude, accuracy, speed } = position.coords;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return {
      latitude,
      longitude,
      accuracy: typeof accuracy === 'number' && Number.isFinite(accuracy) ? accuracy : null,
      speed: typeof speed === 'number' && Number.isFinite(speed) ? speed : null,
    };
  } catch {
    return null;
  }
}
